import { defineStore } from "pinia";
import axios from "axios";

export const useWebSkillRankingStore = defineStore("WebSkillRanking", {
    state: () => ({
        ranking: {
            id: null,
            skill_name: null,
            pool_members_count: 0,
            assigned_records_count: 0,
            avg_duration: null,
            last_published: null,
            status: null,
            meta_title: null,
            meta_description: null,
            path : null,
            has_available_changes: false,
            domains: {
                vertical: null,
                vertical_name: null,
                aoe: null,
                aoe_name: null,
                industry: null,
                industry_name: null,
            },
        },
        profiles: [],
        isEditVerticalModalOpen: false,
        isEditAoEModalOpen: false,
        isEditIndustryModalOpen: false,
        isEditMetaModalOpen: false,
        isEditMetaTitleModalOpen: false,
        isSubmitPublicationModalOpen: false,
        isEditRegionModalOpen: false,
        activeRegionProfile: null,
        loading: false,
        isGeneratingMetaDescription: false,
        isGeneratingMetaTitle: false,
    }),
    getters: {
        activeProfiles: (state) => state.profiles.filter(p => !p.is_excluded),
        excludedProfiles: (state) => state.profiles.filter(p => p.is_excluded),
    },
    actions: {
        initAxiosCsrf() {
            const token = document.head.querySelector('meta[name="csrf-token"]');
            if (token) {
                axios.defaults.headers.common["X-CSRF-TOKEN"] = token.content;
                axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
                axios.defaults.withCredentials = true;
            }
        },
        setRankingData(data) {
            if (!data) return;
            this.ranking.id = data.id;
            this.ranking.skill_name = data.skill_name;
            this.ranking.pool_members_count = data.pool_members_count;
            this.ranking.assigned_records_count = data.assigned_records_count;
            this.ranking.avg_duration = data.avg_duration;
            this.ranking.last_published = data.last_published;
            this.ranking.status = data.status;
            this.ranking.meta_title = data.meta_title;
            this.ranking.meta_description = data.meta_description;
            this.ranking.path = data.path;
            this.ranking.has_available_changes = data.has_available_changes || false;

            this.ranking.domains = data.domains || {
                vertical: null,
                vertical_name: null,
                aoe: null,
                aoe_name: null,
                industry: null,
                industry_name: null,
            };

            this.profiles = data.profiles || [];
        },
        async fetchWebSkillRankingData(rankingId) {
            this.initAxiosCsrf();
            if (!rankingId) return;
            try {
                this.$functions?.showLoader();
                const response = await axios.get(`/admin/web-skill-rankings/${rankingId}/edit-data`);
                this.setRankingData(response.data.data);
            } catch (error) {
                console.error(error);
            } finally {
                this.$functions?.hideLoader();
            }
        },
        async refreshProfiles() {
            this.initAxiosCsrf();
            if (!this.ranking.id) return;
            try {
                const response = await axios.get(`/admin/web-skill-rankings/${this.ranking.id}/edit-data`);
                this.profiles = response.data.data.profiles || [];
            } catch (error) {
                console.error(error);
            }
        },
        openEditVerticalModal() {
            this.isEditVerticalModalOpen = true;
        },
        openEditAoEModal() {
            this.isEditAoEModalOpen = true;
        },
        openEditIndustryModal() {
            this.isEditIndustryModalOpen = true;
        },
        openEditMetaModal() {
            this.isEditMetaModalOpen = true;
        },
        closeEditMetaModal() {
            this.isEditMetaModalOpen = false;
        },
        openEditMetaTitleModal() {
            this.isEditMetaTitleModalOpen = true;
        },
        closeEditMetaTitleModal() {
            this.isEditMetaTitleModalOpen = false;
        },
        openSubmitPublicationModal() { 
            this.isSubmitPublicationModalOpen = true; 
        },
        closeSubmitPublicationModal() { 
            this.isSubmitPublicationModalOpen = false; 
        },
        openEditRegionModal(profile) {
            this.activeRegionProfile = profile;
            this.isEditRegionModalOpen = true;
        },
        closeEditRegionModal() {
            this.isEditRegionModalOpen = false;
            this.activeRegionProfile = null;
        },
        async recalculateRanking() {
            this.initAxiosCsrf();
            if (!this.ranking.id) return;

            this.loading = true;
            try {
                const response = await axios.post(`/admin/web-skill-rankings/${this.ranking.id}/recalculate`);
                this.ranking.has_available_changes = false;
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            } finally {
                this.loading = false;
            }
        },
        async saveDomains() {
            this.initAxiosCsrf();
            if (!this.ranking.id) return;

            this.loading = true;
            try {
                const payload = {
                    domains: {
                        vertical: this.ranking.domains.vertical,
                        aoe: this.ranking.domains.aoe,
                        industry: this.ranking.domains.industry,
                    },
                };

                const response = await axios.put(`/admin/web-skill-rankings/${this.ranking.id}/domains`, payload);
                this.setRankingData(response.data.data);
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            } finally {
                this.loading = false;
            }
        },
        async setDefaultBackgroundPhoto(profileId, useDefault) {
            this.initAxiosCsrf();
            this.loading = true;
            try {
                const response = await axios.post(
                    `/admin/web-skill-rankings/profile/${profileId}/set-default-background-photo`,
                    { use_default_photo: useDefault }
                );

                const updatedProfile = response.data.data;
                const index = this.profiles.findIndex(p => p.id === profileId);
                
                if (index !== -1) {
                    this.profiles[index].photo = updatedProfile.photo;
                    this.profiles[index].use_default_background_photo = updatedProfile.use_default_background_photo;
                }
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            } finally {
                this.loading = false;
            }
        },
        async uploadProfilePhoto(profileId, file) {
            this.initAxiosCsrf();
            this.loading = true;
            try {
                const formData = new FormData();
                formData.append('photo', file);

                const response = await axios.post(
                    `/admin/web-skill-rankings/profile/${profileId}/upload-photo`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );

                const updatedProfile = response.data.data;
                const index = this.profiles.findIndex(p => p.id === profileId);

                if (index !== -1) {
                    this.profiles[index].photo = updatedProfile.photo;
                    this.profiles[index].use_default_background_photo = false;
                }
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            } finally {
                this.loading = false;
            }
        },
        updateProfilePhotoLocally(profileId, newPhoto) {
            const index = this.profiles.findIndex(p => p.id === profileId);
            if (index !== -1) {
                this.profiles[index].photo = newPhoto;
                this.profiles[index].use_default_background_photo = false;
            }
        },
        async updateProfileTexts(profileId, headline, summary) {
            this.initAxiosCsrf();
            this.loading = true;
            try {
                const response = await axios.put(
                    `/admin/web-skill-rankings/profile/${profileId}/texts`,
                    { headline: headline, summary: summary }
                );

                const updatedData = response.data.data;
                const index = this.profiles.findIndex(p => p.id === profileId);

                if (index !== -1) {
                    this.profiles[index].headline = updatedData.headline;
                    this.profiles[index].summary = updatedData.summary;
                }
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            } finally {
                this.loading = false;
            }
        },
        async updateProfileCustomRegion(profileId, customRegion) {
            this.initAxiosCsrf();
            this.loading = true;
            try {
                const response = await axios.put(
                    `/admin/web-skill-rankings/profile/${profileId}/custom-region`,
                    { custom_region: customRegion }
                );

                const updatedData = response.data.data;
                const index = this.profiles.findIndex(p => p.id === profileId);

                if (index !== -1) {
                    this.profiles[index].custom_region = updatedData.custom_region;
                    this.profiles[index].region = updatedData.region;
                }
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            } finally {
                this.loading = false;
            }
        },
        async generateAiContentForProfiles(profileIds) {
            this.initAxiosCsrf();
            this.loading = true;
            try {
                const response = await axios.post(
                    `/admin/web-skill-rankings/profiles/generate-ai-content`,
                    { profile_ids: profileIds }
                );

                this.profiles.forEach(p => {
                    if (profileIds.includes(p.id)) {
                        p.ai_content_status = 'in_progress';
                    }
                });

                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            } finally {
                this.loading = false;
            }
        },
        async updateRankingMetaTitle(metaTitle) {
            this.initAxiosCsrf();
            try {
                const response = await axios.put(`/admin/web-skill-rankings/${this.ranking.id}/meta-title`, {
                    meta_title: metaTitle
                });
                this.ranking.meta_title = response.data.data.meta_title;
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async updateRankingMetaDescription(metaDescription) {
            this.initAxiosCsrf();
            try {
                const response = await axios.put(`/admin/web-skill-rankings/${this.ranking.id}/meta-description`, {
                    meta_description: metaDescription
                });
                this.ranking.meta_description = response.data.data.meta_description;
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async requestPublication(rankingId) {
            this.initAxiosCsrf();
            try {
                const response = await axios.post(`/admin/web-skill-rankings/${rankingId}/request-publication`);
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        async toggleProfileExclusion(profileId, isExcluded) {
            this.initAxiosCsrf();
            this.loading = true;
            try {
                const response = await axios.post(`/admin/web-skill-rankings/profile/${profileId}/toggle-exclusion`, {
                    is_excluded: isExcluded
                });
                this.setRankingData(response.data.data);
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            } finally {
                this.loading = false;
            }
        },
    },
});